import React, { useEffect, useRef, useState } from "react";
import "./styles/DineInSplashTransition.css";

/** Hold time of each splash frame, independently. "Powered by" is a connector
 *  beat, so it's shorter than the two brand frames. Keep every frame longer
 *  than the CSS crossfade (~0.9s) or frames start overlapping. */
export interface DineInSplashFrameHolds {
  /** Frame 1 — the business's logo, name and tagline. */
  business: number;
  /** Frame 2 — "POWERED BY". */
  poweredBy: number;
  /** Frame 3 — the NineFinds mark. Also the minimum time the screen behind
   *  gets to load: if it isn't ready, this frame simply holds longer. */
  nineFinds: number;
}

export const DEFAULT_SPLASH_FRAME_HOLDS: DineInSplashFrameHolds = {
  business: 2000,
  poweredBy: 1500,
  nineFinds: 2500,
};

/** Beat before frame 1 starts, so its entrance has somewhere to come from. */
const LEAD_IN_MS = 90;

interface DineInSplashTransitionProps {
  /** Shown as the wordmark under the mark, and as the monogram fallback's
   *  initial when logoUrl isn't provided — the whole point of this
   *  component being business-agnostic rather than hardcoded to one brand. */
  brandName: string;
  /** The business's own logo (Business.logoUrl). Falls back to a monogram of
   *  brandName's first letter when absent — never a broken image. */
  logoUrl?: string;
  tagline?: string;
  loadingLabel?: string;
  /** How long each splash frame holds before crossfading to the next:
   *  business → "Powered by" → NineFinds. Defaults to DEFAULT_SPLASH_FRAME_HOLDS. */
  frameHolds?: DineInSplashFrameHolds;
  /** Whether the real content behind the splash is ready to reveal. The final
   *  (NineFinds) frame won't exit until this is true — so a slow backend
   *  extends the last frame rather than cutting the sequence short. */
  isReady?: boolean;
  children: React.ReactNode;
}

/**
 * Wraps any "next" screen with a three-frame branded splash sequence that
 * auto-transitions into it:
 *
 *   frame 1  the business's own identity (logo/monogram + name)
 *   frame 2  a quiet "POWERED BY" interstitial beat
 *   frame 3  the NineFinds brand mark + NINEFINDS wordmark
 *   exit     splash fades/scales out, children stagger in
 *
 * One `stage` value (0 → 1 → 2 → 3 → 4) drives every animation via CSS rules
 * keyed off `data-stage` — see DineInSplashTransition.css — rather than
 * @keyframes on the entering elements, so nothing restarts on re-render. All
 * layers stay mounted the whole time; only opacity/transform move (GPU-
 * composited — no filter/width animation, that's what caused the earlier jank).
 *
 * Business-agnostic by design: brandName + logoUrl are props, not hardcoded.
 */
const DineInSplashTransition: React.FC<DineInSplashTransitionProps> = ({
  brandName,
  logoUrl,
  tagline = "PREMIUM DINE-IN EXPERIENCE",
  loadingLabel = "SETTING YOUR TABLE",
  frameHolds = DEFAULT_SPLASH_FRAME_HOLDS,
  isReady = true,
  children,
}) => {
  const prefersReducedMotion = useRef(
    typeof window !== "undefined" &&
      !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  ).current;

  // Reduced motion: skip the whole sequence, straight to the content.
  const [stage, setStage] = useState<0 | 1 | 2 | 3 | 4>(
    prefersReducedMotion ? 4 : 0
  );

  // Frames 1 → 2 → 3 advance on a fixed cadence.
  useEffect(() => {
    if (prefersReducedMotion) return;
    const timers = [
      setTimeout(() => setStage(1), LEAD_IN_MS),
      setTimeout(() => setStage(2), LEAD_IN_MS + frameHolds.business),
      setTimeout(() => setStage(3), LEAD_IN_MS + frameHolds.business + frameHolds.poweredBy),
    ];
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Final frame → exit: needs BOTH the frame's full hold time AND real
  // readiness, so a slow backend stretches the NineFinds frame instead of
  // the sequence ever exiting onto a screen that isn't there yet.
  const stage3At = useRef<number | null>(null);
  useEffect(() => {
    if (prefersReducedMotion || stage !== 3) return;
    if (stage3At.current === null) stage3At.current = Date.now();
    if (!isReady) return;

    const elapsed = Date.now() - stage3At.current;
    const exitTimer = setTimeout(
      () => setStage(4),
      Math.max(frameHolds.nineFinds - elapsed, 0)
    );
    return () => clearTimeout(exitTimer);
  }, [stage, isReady, frameHolds.nineFinds, prefersReducedMotion]);

  // Published to CSS so the loading bar fills over the whole sequence.
  const totalMs =
    LEAD_IN_MS + frameHolds.business + frameHolds.poweredBy + frameHolds.nineFinds;

  const monogram = brandName?.trim()?.[0]?.toUpperCase() || "?";

  return (
    <div
      className="dine-in-splash-transition"
      data-stage={stage}
      style={{ "--dine-in-splash-total": `${totalMs}ms` } as React.CSSProperties}
    >
      <div className="dine-in-splash-transition__layer" data-splash>
        <span className="dine-in-splash-transition__glow" data-glow />
        <span className="dine-in-splash-transition__glow-bottom" aria-hidden="true" />

        {/* ── Frame 1: the business ─────────────────────────────────── */}
        <div className="dine-in-splash-transition__frame" data-frame="business">
          <div
            className={`dine-in-splash-transition__mark${
              logoUrl ? " dine-in-splash-transition__mark--has-logo" : ""
            }`}
            data-mark
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                className="dine-in-splash-transition__logo-img"
              />
            ) : (
              <span className="dine-in-splash-transition__monogram">{monogram}</span>
            )}
            <span className="dine-in-splash-transition__shine" aria-hidden="true" />
          </div>
          <div className="dine-in-splash-transition__word" data-word>
            {brandName}
          </div>
          <span className="dine-in-splash-transition__rule" data-rule />
          <div className="dine-in-splash-transition__tag" data-tag>
            {tagline}
          </div>
        </div>

        {/* ── Frame 2: "Powered by" beat ────────────────────────────── */}
        <div className="dine-in-splash-transition__frame" data-frame="powered">
          <span className="dine-in-splash-transition__powered-rule" aria-hidden="true" />
          <div className="dine-in-splash-transition__powered">POWERED BY</div>
          <span className="dine-in-splash-transition__powered-rule" aria-hidden="true" />
        </div>

        {/* ── Frame 3: NineFinds ────────────────────────────────────── */}
        <div className="dine-in-splash-transition__frame" data-frame="nine">
          <div className="dine-in-splash-transition__mark dine-in-splash-transition__mark--nine">
            <img
              src="/nine_logo_vector_4.svg"
              alt=""
              className="dine-in-splash-transition__logo-img dine-in-splash-transition__logo-img--nine"
            />
            <span className="dine-in-splash-transition__shine" aria-hidden="true" />
          </div>
          <div className="dine-in-splash-transition__word">NINEFINDS</div>
          <span className="dine-in-splash-transition__rule" />
          <div className="dine-in-splash-transition__tag">{tagline}</div>
        </div>

        {/* Meter persists across all three frames. */}
        <div className="dine-in-splash-transition__meter">
          <div className="dine-in-splash-transition__track">
            <div className="dine-in-splash-transition__bar" data-bar />
          </div>
          <div className="dine-in-splash-transition__meter-label">{loadingLabel}</div>
        </div>
      </div>

      <div className="dine-in-splash-transition__layer" data-next>
        {children}
      </div>
    </div>
  );
};

export default DineInSplashTransition;
